<div class="flex-fill container-fluid">
    <form [formGroup]="form" class="d-flex flex-column gap-8">
        <div class="row">
            <div class="col-12 px-0">
                <div class="alert bg-oddr-10 mb-0 d-flex align-items-center gap-6">
                    <img alt="Verify" src="/assets/images/icons/info-oddr.svg" height="25px" width="25px" />
                    <div class="d-flex flex-column">
                        <span class="fw-semi-bold text-static-dark">Begin signing your media with ODDR</span>
                        <span class="fs-6 text-static-dark">Your files will be processed and signed. Upload a single file, or select multiple images for a carousel post. ODDR stores only the extracted data and thumbnail, not the original files.</span>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <!-- Left column -->
            <div class="col-12 col-lg-8 px-0 pe-lg-4 d-flex flex-column gap-6">
                <div class="card">
                    <div class="card-body">
                        <pp-input id="muDetailsTitle"
                            [form]="form"
                            label="Title"
                            [required]="true"
                            subLabel="Used only for your ODDR certificate and signing — this is not the caption or title of your social posts. You'll write each post's caption in the next steps." />
                    </div>
                </div>

                <!-- File Upload -->
                <div class="card">
                    <div class="card-body">
                        <div class="d-flex justify-content-between align-items-center gap-3">
                            <span class="fs-6 fw-semi-bold mb-3">Select your file(s)</span>
                            <span class="badge bg-lighter-grey text-dark-grey fs-8">Required</span>
                        </div>
                        <span class="fs-7 text-muted mb-3 d-block">Your files will be processed and individually signed with C2PA.</span>
                        <ngx-file-drop contentClassName="media-upload-drop-zone-content"
                            dropZoneClassName="media-upload-drop-zone"
                            [directory]="false"
                            [multiple]="true"
                            (onFileDrop)="onFileSelected($event)">
                            <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
                                @if (!isProcessingFileUpload && !fileUploadComplete) {
                                    <div class="d-flex flex-column gap-6">
                                        <div class="mx-auto p-8 rounded-circle text-secondary bg-oddr-100">
                                            <img alt="Upload" [src]="resolveImageIcon('upload', true)" height="36px" width="36px" />
                                        </div>
                                        <div class="d-flex flex-column align-items-center">
                                            <span class="fs-4 fw-bold text-static-dark text-center">Drag your files here to upload</span>
                                            <span class="fs-6 text-static-dark text-center">or select files from your device. You can upload a single file or up to 10 images at once.</span>
                                        </div>
                                        <div class="d-flex justify-content-center">
                                            <pp-button [shape]="buttonShape.Square" [variant]="buttonVariant.Primary" (buttonClick)="openFileSelector()">
                                                <div class="d-flex justify-content-center align-items-center gap-4">
                                                    <img alt="Upload" [src]="resolveImageIcon('folder-upload', true)"/>
                                                    Select files
                                                </div>
                                            </pp-button>
                                        </div>
                                        <div class="d-flex justify-content-center">
                                            <h5 class="fw-semi-bold text-center text-static-dark">File types supported: {{ supportedFileTypesLabel }}</h5>
                                        </div>
                                        <div class="d-flex flex-column align-items-center gap-1">
                                            <span class="fs-8 text-muted text-center">Carousel limits: X (4 images), Facebook (10), Instagram (10), LinkedIn (20)</span>
                                        </div>
                                        @if (fileUploadError) {
                                            <div class="d-flex justify-content-center">
                                                <span class="text-validation-error">{{ fileUploadError }}</span>
                                            </div>
                                        }
                                    </div>
                                }
                                @else if (isProcessingFileUpload) {
                                    <div class="circular-progress-container">
                                        <svg class="circular-progress" viewBox="0 0 80 80">
                                            <circle class="circular-progress-bg" cx="40" cy="40" r="34" />
                                            <circle class="circular-progress-bar" [class.complete]="fileUploadComplete" cx="40" cy="40" r="34"
                                                [attr.stroke-dasharray]="2 * 3.14159 * 34"
                                                [attr.stroke-dashoffset]="2 * 3.14159 * 34 * (1 - uploadProgress / 100)" />
                                        </svg>
                                        <span class="circular-progress-text">{{ uploadProgressDisplay }}%</span>
                                    </div>
                                    @if (fileUploadComplete) {
                                        <div class="d-flex justify-content-center align-items-center gap-2">
                                            <img alt="Complete" src="/assets/images/icons/check-circle-solid-primary.svg" />
                                            <span class="fs-6 fw-semi-bold text-static-dark">{{ fileName }} uploaded ({{ fileSize }})</span>
                                        </div>
                                    }
                                }
                            </ng-template>
                        </ngx-file-drop>
                    </div>
                </div>
                @if (fileUploadError) {
                    <div class="mt-6">
                        <span class="text-validation-error">{{ fileUploadError }}</span>
                    </div>
                }

                <!-- Thumbnail Upload (Video only) -->
                @if (isVideoFile) {
                    <div #thumbnailSection class="card">
                        <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center gap-3">
                                <span class="fs-6 fw-semi-bold mb-3">Select your thumbnail</span>
                                <span class="badge bg-lighter-grey text-dark-grey fs-8">Required</span>
                            </div>
                            <span class="fs-7 text-muted mb-6 d-block">Set a thumbnail for your video.</span>
                            <ngx-file-drop contentClassName="thumbnail-upload-drop-zone-content"
                                dropZoneClassName="thumbnail-upload-drop-zone"
                                [directory]="false"
                                [multiple]="false"
                                (onFileDrop)="onThumbnailSelected($event)">
                                <ng-template ngx-file-drop-content-tmp let-openFileSelector="openFileSelector">
                                    <div class="d-flex justify-content-between align-items-center">
                                        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-8">
                                            <div class="d-flex flex-column gap-3">
                                                <div class="d-flex flex-column gap-3">
                                                    <pp-button [disabled]="isProcessingThumbnailUpload || (thumbnailHasBeenUploaded && !thumbnailImageLoaded)"
                                                        [shape]="buttonShape.Square"
                                                        [width]="buttonWidth.FitContent"
                                                        (buttonClick)="openFileSelector()">
                                                        <div class="d-flex gap-4 justify-content-center align-items-center fs-5 fw-medium">
                                                            <img alt="Upload from files" [src]="resolveImageIcon('folder-upload', true)" />
                                                            Upload from files
                                                        </div>
                                                    </pp-button>
                                                    @if (unsupportedThumbnailFile) {
                                                        <span class="text-validation-error">Please upload a .JPG or .PNG file.</span>
                                                    }
                                                </div>
                                                <div class="d-flex flex-column">
                                                    <div class="heading-6 fw-semi-bold text-static-dark">File types supported: .JPG or .PNG</div>
                                                    <div class="heading-6 fw-semi-bold text-static-dark">Recommended ratio: 16:9</div>
                                                    <div class="heading-6 fw-semi-bold text-static-dark">Max. file size: 5mb</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="thumbnail-preview-container">
                                            @if (thumbnailHasBeenUploaded) {
                                                <img id="uploadVideoThumbnail"
                                                    alt="Thumbnail"
                                                    class="thumbnail-preview w-100 object-fit-contain"
                                                    [class.d-none]="!thumbnailImageLoaded"
                                                    [src]="thumbnailImagePath"
                                                    (load)="onThumbnailImageLoaded()" />
                                            }
                                        </div>
                                    </div>
                                </ng-template>
                            </ngx-file-drop>
                        </div>
                    </div>
                }
            </div>

            <!-- Right column -->
            <div class="col-12 col-lg-4 px-0 ps-lg-4">
                <div class="card">
                    <div class="card-body d-flex flex-column gap-8">
                        <h4 class="fw-semi-bold">What happens next?</h4>
                        <div class="d-flex align-items-start gap-6">
                            <img alt="Processed" [src]="resolveImageIcon('search')" height="24px" width="24px" />
                            <div class="d-flex flex-column">
                                <span class="fw-semi-bold">Media is processed</span>
                                <span class="fs-7 text-muted">ODDR reads the metadata of the file.</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-start gap-6">
                            <img alt="Certificate" [src]="resolveImageIcon('padlock')" height="24px" width="24px" />
                            <div class="d-flex flex-column">
                                <span class="fw-semi-bold">Certificate is created</span>
                                <span class="fs-7 text-muted">Your content is signed with C2PA proof.</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-start gap-6">
                            <img alt="Share" [src]="resolveImageIcon('folder-upload')" height="24px" width="24px" />
                            <div class="d-flex flex-column">
                                <span class="fw-semi-bold">Ready to share</span>
                                <span class="fs-7 text-muted">Publish your signed media to ODDR or a platform, or download a signed copy.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
</div>
<div class="d-flex justify-content-between align-items-center mt-6 gap-10">
    <span class="fs-8">By uploading your data to ODDR, you acknowledge that you
        agree to ODDR's <a class="text-link" tabindex="0" href="https://oddr.org/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        and confirm you have the right to upload this media.
        <a class="text-link" tabindex="0" href="https://oddr.org/privacy-policy" target="_blank" rel="noopener noreferrer">Learn more</a>
    </span>
    <div class="d-flex justify-content-end">
        <pp-button [disabled]="!canProceed"
            [shape]="buttonShape.Square"
            [variant]="buttonVariant.Primary"
            (buttonClick)="submitAndGoToNextStep()">Next</pp-button>
    </div>
</div>
